import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const FALLBACK_SUGGESTED = [
  "Who's overused this week?",
  "Who's free tomorrow afternoon?",
  'Any conflicts I should know about?',
];

// Sparkle field around the FAB — a fixed set of small stars drifting
// outward at their own angle/speed/delay, not a single tidy orbit ring.
// Angles are spread around the circle but jittered per-particle so the
// field reads as organic rather than mechanical.
const SPARKLES = [
  { angle: 8, dist: 36, size: 15, dur: 2.1, delay: 0, color: '#a78bfa' },
  { angle: 52, dist: 44, size: 13, dur: 1.8, delay: 0.35, color: '#38bdf8' },
  { angle: 96, dist: 32, size: 17, dur: 2.4, delay: 0.7, color: '#fafafa' },
  { angle: 140, dist: 40, size: 13, dur: 1.9, delay: 1.05, color: '#7c3aed' },
  { angle: 184, dist: 46, size: 15, dur: 2.2, delay: 0.15, color: '#93c5fd' },
  { angle: 228, dist: 34, size: 13, dur: 2, delay: 0.5, color: '#bae6fd' },
  { angle: 272, dist: 42, size: 17, dur: 2.3, delay: 0.85, color: '#0284c7' },
  { angle: 316, dist: 36, size: 15, dur: 1.9, delay: 1.2, color: '#a78bfa' },
];

export default function AiAssistantPanel({ weekStartIso, weekEndIso, rangeLabel, onViewLecturer, onViewRoom }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const bodyRef = useRef(null);

  // Every new message (ours or the assistant's), a quick-reply tap, or the
  // typing indicator appearing/disappearing should keep the latest content
  // in view — otherwise the panel stays scrolled to wherever it was and the
  // user has to scroll down by hand after every turn.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  // Fresh, AI-written starter chips each time the panel opens on an empty
  // conversation — grounded in this week's real data instead of a fixed
  // hardcoded list, so they vary run to run.
  useEffect(() => {
    if (!open || messages.length || suggested || suggestedLoading) return;
    setSuggestedLoading(true);
    api.get('/ai/scheduling-assistant/suggestions', { params: { weekStartIso, weekEndIso } })
      .then((res) => setSuggested(res.data.data?.length ? res.data.data : FALLBACK_SUGGESTED))
      .catch(() => setSuggested(FALLBACK_SUGGESTED))
      .finally(() => setSuggestedLoading(false));
  }, [open, messages.length, suggested, suggestedLoading, weekStartIso, weekEndIso]);

  // Week navigated while the panel sat open and empty — drop the stale
  // suggestions so the effect above re-fetches for the new week.
  useEffect(() => { setSuggested(null); }, [weekStartIso, weekEndIso]);

  // Quick-reply buttons are one-shot: clicking marks that action "used" (so
  // it visibly confirms the tap and can't be double-fired) without touching
  // any other message's actions.
  const runAction = (msgIndex, actionIndex, action) => {
    setMessages((prev) => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      return { ...m, actions: m.actions.map((a, ai) => (ai === actionIndex ? { ...a, used: true } : a)) };
    }));
    if (action.kind === 'view_lecturer') { onViewLecturer?.(); return; }
    if (action.kind === 'view_room') { onViewRoom?.(); return; }
    if (action.kind === 'prompt' && action.prompt) sendMessage(action.prompt);
  };

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setTyping(true);
    try {
      const res = await api.post('/ai/scheduling-assistant', { message: trimmed, weekStartIso, weekEndIso, history });
      const { reply, stat, actions } = res.data.data;
      setMessages((prev) => [...prev, { role: 'assistant', text: reply, stat, actions: actions || [] }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: err.response?.data?.message || "Sorry, I couldn't reach the AI assistant." }]);
    } finally {
      setTyping(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); sendMessage(input); };

  return (
    <>
      <div className={`sap-scrim ${open ? 'show' : ''}`} onClick={() => setOpen(false)} />

      <div className={`sap-fab-wrap ${open ? 'hidden' : ''}`}>
        {SPARKLES.map((s, i) => (
          <svg
            key={i} className="sap-sparkle" viewBox="0 0 24 24"
            style={{
              '--a': `${s.angle}deg`, '--d': `${s.dist}px`, '--sz': `${s.size}px`,
              filter: `drop-shadow(0 0 1px ${s.color})`,
              animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s`,
            }}
          >
            {/* Classic 4-point sparkle burst — concave curves between the
                points, which a CSS rotated-square/clip-path can't produce
                without looking like a plain diamond or a blurry dot. */}
            <path
              fill={s.color}
              d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
            />
          </svg>
        ))}
        <button type="button" className="sap-fab" onClick={() => setOpen(true)} title="Ask AI" tabIndex={open ? -1 : 0} />
        {/* Icon is a separate layer above the sparkles (not nested inside
            .sap-fab) so it stays crisp on top while the sparkle field
            passes over the button's circle behind it — pointer-events:none
            so clicks still land on the button underneath. */}
        <i className="fa-solid fa-wand-magic-sparkles sap-fab-icon" />
      </div>

      <div className={`sap-panel ${open ? 'open' : ''}`}>
        <div className="sap-panel-hd">
          <div className="sap-panel-av"><i className="fa-solid fa-wand-magic-sparkles" /></div>
          <div>
            <div className="sap-panel-title">TMIS AI Assistant</div>
            <div className="sap-panel-sub">{rangeLabel}</div>
          </div>
          <div className="sap-panel-actions">
            <button type="button" className="icon-btn" onClick={() => setOpen(false)} title="Close">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div className="sap-panel-body" ref={bodyRef}>
          {!messages.length && (
            <div className="sap-empty">
              <div className="sap-empty-ico"><i className="fa-solid fa-wand-magic-sparkles" /></div>
              <div className="sap-empty-t">Ask me about the schedule</div>
              <div className="sap-empty-d">I can check trainer workload, suggest who's free, or answer questions about a specific trainer or module.</div>
              <div className="sap-chip-col">
                {!suggested ? (
                  <>
                    <div className="sap-chip-skel" /><div className="sap-chip-skel" /><div className="sap-chip-skel" />
                  </>
                ) : (
                  suggested.map((s) => (
                    <button key={s} type="button" className="sap-chip" onClick={() => sendMessage(s)}>
                      <i className="fa-solid fa-comment-dots" />{s}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`sap-msg-row ${m.role === 'user' ? 'user' : ''}`}>
              {m.role === 'assistant' && <div className="sap-msg-av"><i className="fa-solid fa-robot" /></div>}
              <div className="sap-bubble-col">
                <div className={`sap-bubble ${m.role === 'user' ? 'user' : 'assistant'}`}>{m.text}</div>
                {m.stat && (
                  <span className={`sap-stat-chip ${m.stat.level}`}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '.6rem' }} />{m.stat.label}
                  </span>
                )}
                {!!m.actions?.length && (
                  <div className="sap-actions">
                    {m.actions.map((a, ai) => (
                      <button
                        key={ai} type="button" className={`sap-action-btn ${a.used ? 'used' : ''}`}
                        onClick={() => runAction(i, ai, a)} disabled={a.used}
                      >
                        {a.used && <i className="fa-solid fa-check" />}
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {typing && (
            <div className="sap-msg-row">
              <div className="sap-msg-av"><i className="fa-solid fa-robot" /></div>
              <div className="sap-bubble assistant sap-typing">
                <span className="sap-dot" /><span className="sap-dot" /><span className="sap-dot" />
              </div>
            </div>
          )}
        </div>

        <div className="sap-panel-ft">
          <form onSubmit={onSubmit}>
            <div className="sap-input-row">
              <input
                className="sap-input" type="text" placeholder="Ask about a trainer, module, or the week…"
                value={input} onChange={(e) => setInput(e.target.value)}
              />
              <button type="submit" className="sap-send" disabled={!input.trim() || typing}>
                <i className="fa-solid fa-paper-plane" />
              </button>
            </div>
          </form>
          <div className="sap-disclaimer"><i className="fa-solid fa-circle-info" /> AI-generated, based on this week's live schedule</div>
        </div>
      </div>
    </>
  );
}
